import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import asyncAction from 'modules/core/lib/asyncAction'

import QuestionCategoryComponent from 'modules/surveys/components/Form/QuestionCategories/Show/QuestionCategoryComponent'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import questionConfigs from 'modules/surveys/store/collections/questionConfigs'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionGroupConfig from 'modules/surveys/store/models/QuestionGroupConfig'
import type Survey from 'modules/surveys/store/models/v2/Survey'

import QuestionConfigsList from './List'

type QuestionConfigsListProps = React.ComponentProps<typeof QuestionConfigsList>

interface Props {
  questionGroupConfig: QuestionGroupConfig
  form: Survey | SurveyForm
}

const QuestionConfigs = ({ questionGroupConfig, form }: Props) => {
  const { updateCanBeStarted } = useSurveyShowContext()

  const fetchQuestionConfigs = () =>
    questionConfigs.fetch({
      remove: false,
      data: {
        surveys_question_group_config_id: questionGroupConfig.attributes.id,
        surveys_form_id: form?.id,
      },
    })

  const visibleQuestionConfigsWithoutCategory =
    questionGroupConfig.questionConfigsWithoutCategory.filter(
      (questionConfig) => !questionConfig.attributes.archived
    )

  const archivedQuestionConfigsWithoutCategory =
    questionGroupConfig.questionConfigsWithoutCategory.filter(
      (questionConfig) => questionConfig.attributes.archived
    )

  const questionCategories = questionGroupConfig.questionCategories

  const updateQuestionConfig: QuestionConfigsListProps['updateQuestionConfig'] = async (data) => {
    const params = {
      label: data.custom_field_params.label,
      required: data.custom_field_params.required,
      choice_options: data.custom_field_params.choice_options,
      id: data.question_config_id,
      surveys_form_id: form?.id,
    }
    const questionConfig = questionConfigs.find({
      id: data.question_config_id,
    })

    if (!questionConfig) {
      return
    }

    const { success, value } = await asyncAction(() => questionConfig.save(params))

    // We reorder all question configs in backend
    if (success) await fetchQuestionConfigs()

    return value
  }

  const createQuestionConfigFromDuplicate: QuestionConfigsListProps['createQuestionConfigFromDuplicate'] =
    async (data, questionConfigCategoryId) => {
      if (!questionGroupConfig) return

      const { success } = await asyncAction(() =>
        questionConfigs.create({
          question_group_config_id: questionGroupConfig.attributes.id,
          question_category_id: questionConfigCategoryId,
          surveys_form_id: form?.id,
          custom_field: data,
        })
      )

      if (success) await fetchQuestionConfigs()
    }

  const toggleArchivedQuestionConfig: QuestionConfigsListProps['toggleArchivedQuestionConfig'] =
    async (data) => {
      const questionConfig = questionConfigs.find({
        id: data.id,
      })

      if (!questionConfig) return

      const { success, value } = await asyncAction(() => questionConfig.rpc('toggle_archive', data))

      // We reorder all question configs in backend
      if (success) {
        await fetchQuestionConfigs()
        await updateCanBeStarted()
      }

      return value
    }

  const deleteQuestionConfig: QuestionConfigsListProps['deleteQuestionConfig'] = async (data) => {
    const questionConfig = questionConfigs.find({
      id: data.id,
    })
    if (!questionConfig) return
    const { success, value } = await asyncAction(() =>
      questionConfig.destroy({ data: { surveys_form_id: form?.id } })
    )

    // We reorder all question configs in backend
    if (success) {
      await fetchQuestionConfigs()
      await updateCanBeStarted()
    }

    return value
  }

  const reorderQuestionConfig: QuestionConfigsListProps['reorderQuestionConfig'] = async (data) => {
    const questionConfig = questionConfigs.find({
      id: data.id,
    })

    if (!questionConfig) return

    const { success, value } = await asyncAction(() =>
      questionConfig.rpc('reorder', {
        new_position: data.newPosition,
        surveys_form_id: form?.id,
      })
    )

    // We reorder all question configs in backend
    if (success) await fetchQuestionConfigs()

    return value
  }

  const { loading } = useAsyncData(fetchQuestionConfigs, [])

  if (!form) return null

  if (loading) return <Loading />

  return (
    <Box gap="s24">
      {!!visibleQuestionConfigsWithoutCategory.length && (
        <QuestionConfigsList
          form={form}
          questionConfigs={visibleQuestionConfigsWithoutCategory}
          deleteQuestionConfig={deleteQuestionConfig}
          reorderQuestionConfig={reorderQuestionConfig}
          createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
          toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
          updateQuestionConfig={updateQuestionConfig}
        />
      )}
      {!!archivedQuestionConfigsWithoutCategory.length && (
        <QuestionConfigsList
          archived
          form={form}
          questionConfigs={archivedQuestionConfigsWithoutCategory}
          deleteQuestionConfig={deleteQuestionConfig}
          reorderQuestionConfig={reorderQuestionConfig}
          createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
          toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
          updateQuestionConfig={updateQuestionConfig}
        />
      )}
      {questionCategories.map((questionCategory) => (
        <QuestionCategoryComponent
          key={`questionCategory-${questionCategory.id}`}
          form={form}
          questionGroupConfig={questionGroupConfig}
          questionCategory={questionCategory}
          deleteQuestionConfig={deleteQuestionConfig}
          reorderQuestionConfig={reorderQuestionConfig}
          createQuestionConfigFromDuplicate={createQuestionConfigFromDuplicate}
          toggleArchivedQuestionConfig={toggleArchivedQuestionConfig}
          updateQuestionConfig={updateQuestionConfig}
        />
      ))}
    </Box>
  )
}

export default observer(QuestionConfigs)
